// src/pages/AssignmentView.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BottomNavigation from '../components/layout/BottomNavigation';
import { getAssignmentStatus } from '../utils/assignmentUtils';
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  addDoc,
  updateDoc,
  query,
  where,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import SubmissionForm from '../components/learner/SubmissionForm';
import { db } from '../firebase';
import '../styles/assignmentView.css';

function AssignmentView() {
  const { assignmentId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('submission');
  const [assignment, setAssignment] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [showResubmitNotice, setShowResubmitNotice] = useState(false);

  const [diarySkills, setDiarySkills] = useState('');
  const [diaryFeelings, setDiaryFeelings] = useState('');
  const [savingDiary, setSavingDiary] = useState(false);
  const [diarySuccess, setDiarySuccess] = useState(false);

  const [checkpoints, setCheckpoints] = useState({
    diaryCompleted: false,
    assignmentUploaded: false,
    webinar1Attended: false,
    webinar2Attended: false,
    feedbackReviewed: false
  });

  // Auto-switch to feedback tab when feedback arrives and isn't reviewed yet
  useEffect(() => {
    if (feedback && submission?.reviewStatus === 'reviewed' && !checkpoints.feedbackReviewed) {
      setActiveTab('feedback');
    }
  }, [feedback, submission, checkpoints.feedbackReviewed]);

  // Fetch assignment, submission, diary, checkpoints — only runs on mount/assignmentId change,
  // NOT on activeTab change (activeTab removed from dependency array)
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const fetchAssignmentAndSubmission = async () => {
      try {
        setLoading(true);

        if (!assignmentId) {
          setError("Assignment ID is missing. Please go back to the dashboard and try again.");
          setLoading(false);
          return;
        }

        const db = getFirestore();
        const assignmentDoc = await getDoc(doc(db, "assignments", assignmentId));

        if (!assignmentDoc.exists()) {
          setError("Assignment not found");
          setLoading(false);
          return;
        }

        const assignmentData = { id: assignmentDoc.id, ...assignmentDoc.data() };

        // Convert dates
        const toDate = (val, fallbackDays) => {
          if (val && typeof val.toDate === 'function') return val.toDate();
          if (val) return new Date(val);
          const d = new Date();
          d.setDate(d.getDate() + fallbackDays);
          return d;
        };

        assignmentData.startDate = toDate(assignmentData.startDate, -1);
        assignmentData.endDate = toDate(assignmentData.endDate, 14);

        setAssignment(assignmentData);

        // Fetch submission
        const submissionsQuery = query(
          collection(db, "submissions"),
          where("learnerId", "==", currentUser.uid),
          where("assignmentId", "==", assignmentId)
        );
        const submissionSnapshot = await getDocs(submissionsQuery);

        if (!submissionSnapshot.empty) {
          let latestSubmission = null;
          let latestTimestamp = new Date(0);

          submissionSnapshot.forEach(docSnap => {
            const data = {
              id: docSnap.id,
              ...docSnap.data()
            };
            if (data.createdAt?.toDate) data.createdAt = data.createdAt.toDate();
            if (data.updatedAt?.toDate) data.updatedAt = data.updatedAt.toDate();
            if (data.mentorFeedback?.updatedAt?.toDate) {
              data.mentorFeedback.updatedAt = data.mentorFeedback.updatedAt.toDate();
            }

            const ts = data.updatedAt || data.createdAt || new Date(0);
            if (ts > latestTimestamp) {
              latestSubmission = data;
              latestTimestamp = ts;
            }
          });

          setSubmission(latestSubmission);

          // Check for feedback — embedded first, then legacy collection
          if (latestSubmission.mentorFeedback) {
            setFeedback({
              id: latestSubmission.feedbackId || 'embedded',
              positivePoints: latestSubmission.mentorFeedback.positivePoints || '',
              improvementAreas: latestSubmission.mentorFeedback.improvementAreas || '',
              content: `${latestSubmission.mentorFeedback.positivePoints || ''}\n\n${latestSubmission.mentorFeedback.improvementAreas || ''}`,
              createdAt: latestSubmission.mentorFeedback.updatedAt || new Date(),
              updatedAt: latestSubmission.mentorFeedback.updatedAt || new Date(),
              isEmbedded: true
            });
          } else if (latestSubmission.reviewStatus === "reviewed") {
            const feedbackSnapshot = await getDocs(
              query(collection(db, "feedback"), where("submissionId", "==", latestSubmission.id))
            );
            if (!feedbackSnapshot.empty) {
              const feedbackDoc = feedbackSnapshot.docs[0];
              const feedbackData = { id: feedbackDoc.id, ...feedbackDoc.data() };
              if (feedbackData.createdAt?.toDate) feedbackData.createdAt = feedbackData.createdAt.toDate();
              if (feedbackData.updatedAt?.toDate) feedbackData.updatedAt = feedbackData.updatedAt.toDate();
              setFeedback(feedbackData);
            }
          }
        }

        // Fetch diary entry
        const diarySnapshot = await getDocs(
          query(
            collection(db, "diaryEntries"),
            where("learnerId", "==", currentUser.uid),
            where("assignmentId", "==", assignmentId)
          )
        );
        if (!diarySnapshot.empty) {
          const diaryData = diarySnapshot.docs[0].data();
          setDiarySkills(diaryData.skills || '');
          setDiaryFeelings(diaryData.feelings || '');
        }

        // Fetch checkpoints
        const checkpointSnapshot = await getDocs(
          query(
            collection(db, "checkpoints"),
            where("learnerId", "==", currentUser.uid),
            where("assignmentId", "==", assignmentId)
          )
        );
        if (!checkpointSnapshot.empty) {
          const cp = checkpointSnapshot.docs[0];
          const cpData = cp.data();
          setCheckpoints({
            id: cp.id,
            diaryCompleted: cpData.diaryCompleted || false,
            assignmentUploaded: cpData.assignmentUploaded || false,
            webinar1Attended: cpData.webinar1Attended || false,
            webinar2Attended: cpData.webinar2Attended || false,
            feedbackReviewed: cpData.feedbackReviewed || false
          });
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching assignment:", err);
        setError("Failed to load assignment. Please try again.");
        setLoading(false);
      }
    };

    fetchAssignmentAndSubmission();
  }, [assignmentId, currentUser, navigate]); // activeTab intentionally excluded

  const isOpen = (() => {
    if (!assignment) return false;
    const now = new Date();
    if (assignment.status === 'archived') return false;
    if (assignment.startDate && assignment.startDate > now) return false;
    if (assignment.endDate && assignment.endDate < now) return false;
    return true;
  })();

  const getAssignmentStatusInfo = () => {
    if (!assignment) return { status: "Unknown", canSubmit: false };

    const statusInfo = getAssignmentStatus(assignment);
    let submissionStatus = "Not Started";
    let canSubmit = statusInfo.isAvailable;

    if (submission) {
      if (submission.submissionStatus === "draft") {
        submissionStatus = "Draft Saved";
        canSubmit = true;
      } else if (submission.readyForReview) {
        if (submission.reviewStatus === "reviewed") {
          submissionStatus = "Reviewed";
          canSubmit = true;
          setShowResubmitNotice(true);
        } else {
          submissionStatus = "Submitted";
        }
      } else {
        submissionStatus = "Draft Saved";
        canSubmit = true;
      }
    }

    return { assignmentStatus: statusInfo.status, submissionStatus, canSubmit };
  };

  const handleSaveDiary = async (e) => {
    e.preventDefault();

    try {
      setSavingDiary(true);
      setDiarySuccess(false);

      const diaryData = {
        learnerId: currentUser.uid,
        assignmentId,
        skills: diarySkills.trim(),
        feelings: diaryFeelings.trim(),
        updatedAt: serverTimestamp()
      };

      const diarySnapshot = await getDocs(
        query(
          collection(db, "diaryEntries"),
          where("learnerId", "==", currentUser.uid),
          where("assignmentId", "==", assignmentId)
        )
      );

      if (!diarySnapshot.empty) {
        await updateDoc(doc(db, "diaryEntries", diarySnapshot.docs[0].id), diaryData);
      } else {
        diaryData.createdAt = serverTimestamp();
        await addDoc(collection(db, "diaryEntries"), diaryData);
      }

      // Update diary checkpoint
      const cpUpdate = { diaryCompleted: true };
      if (checkpoints.id) {
        await updateDoc(doc(db, "checkpoints", checkpoints.id), cpUpdate);
      } else {
        const newCp = {
          learnerId: currentUser.uid,
          assignmentId,
          diaryCompleted: true,
          assignmentUploaded: checkpoints.assignmentUploaded,
          webinar1Attended: checkpoints.webinar1Attended,
          webinar2Attended: checkpoints.webinar2Attended,
          feedbackReviewed: checkpoints.feedbackReviewed
        };
        const docRef = await addDoc(collection(db, "checkpoints"), newCp);
        setCheckpoints({ id: docRef.id, ...newCp });
      }

      setDiarySuccess(true);
      setCheckpoints(prev => ({ ...prev, diaryCompleted: true }));
    } catch (err) {
      console.error("Error saving diary entry:", err);
      setError("Failed to save diary entry. Please try again.");
    } finally {
      setSavingDiary(false);
    }
  };

  const toggleCheckpoint = async (checkpoint) => {
    const updatedCheckpoints = { ...checkpoints, [checkpoint]: !checkpoints[checkpoint] };
    setCheckpoints(updatedCheckpoints);

    try {
      if (checkpoints.id) {
        await updateDoc(doc(db, "checkpoints", checkpoints.id), {
          [checkpoint]: !checkpoints[checkpoint]
        });
      } else {
        const newCheckpoint = {
          learnerId: currentUser.uid,
          assignmentId,
          diaryCompleted: updatedCheckpoints.diaryCompleted,
          assignmentUploaded: updatedCheckpoints.assignmentUploaded,
          webinar1Attended: updatedCheckpoints.webinar1Attended,
          webinar2Attended: updatedCheckpoints.webinar2Attended,
          feedbackReviewed: updatedCheckpoints.feedbackReviewed
        };
        const docRef = await addDoc(collection(db, "checkpoints"), newCheckpoint);
        setCheckpoints({ id: docRef.id, ...updatedCheckpoints });
      }
    } catch (err) {
      console.error("Error updating checkpoint:", err);
      setCheckpoints(checkpoints); // revert
    }
  };

  if (loading) return <div className="loading">Loading assignment...</div>;
  if (error) return <div className="error-message">{error}</div>;

  const { assignmentStatus, submissionStatus, canSubmit } = getAssignmentStatusInfo();
  const hasFeedback = feedback || (submission?.reviewStatus === 'reviewed' && submission?.mentorFeedback);

  return (
    <div className="assignment-view-container">
      <div className="assignment-header">
        <h1>{assignment.title}</h1>
        <div className="status-badges">
          <div className={`status-badge status-${assignmentStatus.toLowerCase().replace(' ', '-')}`}>
            {assignmentStatus}
          </div>
          {submission && (
            <div className={`status-badge status-${submissionStatus.toLowerCase().replace(' ', '-')}`}>
              {submissionStatus}
            </div>
          )}
        </div>
      </div>

      {hasFeedback && activeTab !== 'feedback' && !checkpoints.feedbackReviewed && (
        <div className="feedback-notification" onClick={() => setActiveTab('feedback')}>
          <span className="notification-icon">🔔</span>
          <span>New feedback from your mentor is available! Click here to view.</span>
        </div>
      )}

      <div className="assignment-dates">
        {assignment.startDate && (
          <div><strong>Available:</strong> {assignment.startDate.toLocaleDateString()}</div>
        )}
        {assignment.endDate && (
          <div><strong>Due:</strong> {assignment.endDate.toLocaleDateString()}</div>
        )}
      </div>

      <div className="assignment-content">
        <h3>Instructions</h3>
        <div className="instruction-content">
          {assignment.instruction
            ? <div style={{ whiteSpace: 'pre-wrap' }}>{assignment.instruction}</div>
            : <p>No instructions provided.</p>}
        </div>
      </div>

      <div className="assignment-sections-container">
        <div className="tabs-navigation">
          <button
            className={`tab-button ${activeTab === 'submission' ? 'active' : ''}`}
            onClick={() => setActiveTab('submission')}
          >
            Submit Your Assignment
          </button>
          <button
            className={`tab-button ${activeTab === 'diary' ? 'active' : ''}`}
            onClick={() => setActiveTab('diary')}
          >
            Reflection Diary
          </button>
          <button
            className={`tab-button ${activeTab === 'checkpoints' ? 'active' : ''}`}
            onClick={() => setActiveTab('checkpoints')}
          >
            Checkpoints
          </button>
          {hasFeedback && (
            <button
              className={`tab-button ${activeTab === 'feedback' ? 'active' : ''} ${!checkpoints.feedbackReviewed ? 'has-notification' : ''}`}
              onClick={() => setActiveTab('feedback')}
            >
              Mentor Feedback
              {!checkpoints.feedbackReviewed && <span className="notification-dot"></span>}
            </button>
          )}
        </div>

        <div className="tab-content">
          {activeTab === 'submission' && (
            <div className="assignment-section submission-section">
              <h3>{submission ? "Your Submission" : "Submit Your Assignment"}</h3>

              {submission && (
                <div className="submission-status">
                  <div className="status-info">
                    <span className={`status-badge status-${submission.submissionStatus || 'submitted'}`}>
                      {submission.submissionStatus === 'updated' ? 'Updated' : 'Submitted'}
                    </span>
                    {submission.updatedAt && (
                      <span className="submission-date">
                        Last updated: {typeof submission.updatedAt.toLocaleString === 'function'
                          ? submission.updatedAt.toLocaleString()
                          : new Date(submission.updatedAt).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div className="review-status">
                    {submission.reviewStatus === 'reviewed'
                      ? <span className="status-badge status-reviewed">Reviewed by Mentor</span>
                      : <span className="status-badge status-pending">Pending Review</span>}
                  </div>
                </div>
              )}

              {submission?.reviewStatus === 'reviewed' && (
                <div className="resubmission-alert">
                  <p><strong>This submission has been reviewed by your mentor.</strong></p>
                  <p>You can make changes and resubmit for additional feedback.</p>
                </div>
              )}

              {showResubmitNotice && (
                <div className="feedback-notification resubmit-notice">
                  <span className="notification-icon">📝</span>
                  <span>You're editing a submission that has already been reviewed. Make your changes and submit again for another review.</span>
                </div>
              )}

              {canSubmit ? (
                <SubmissionForm
                  assignmentId={assignmentId}
                  existingSubmission={submission}
                  onSubmitSuccess={() => toggleCheckpoint('assignmentUploaded')}
                />
              ) : (
                submission && (
                  <div className="submission-content">
                    {submission.content && (
                      <div className="text-submission">
                        <h4>Your Answer</h4>
                        <div className="content-text">{submission.content}</div>
                      </div>
                    )}
                    {submission.fileUrls?.length > 0 && (
                      <div className="file-submissions">
                        <h4>Uploaded Files</h4>
                        <ul className="file-list">
                          {submission.fileUrls.map((file, i) => (
                            <li key={i} className="file-item">
                              <a href={file.url} target="_blank" rel="noopener noreferrer">{file.name}</a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {!isOpen && (
                      <div className="closed-message">This assignment is currently not open for submissions.</div>
                    )}
                  </div>
                )
              )}
            </div>
          )}

          {activeTab === 'diary' && (
            <div className="assignment-section diary-section">
              <h3>Reflection Diary</h3>
              <p className="section-description">
                Reflect on your learning experience with this assignment. This is optional but highly recommended.
              </p>

              {diarySuccess && <div className="success-message">Reflection saved successfully!</div>}

              <form onSubmit={handleSaveDiary} className="diary-form">
                <div className="form-group">
                  <label htmlFor="diarySkills">What new skills or knowledge did you gain in this assignment?</label>
                  <textarea
                    id="diarySkills"
                    value={diarySkills}
                    onChange={(e) => setDiarySkills(e.target.value)}
                    className="form-control"
                    placeholder="Describe the skills or knowledge you've gained..."
                    rows={4}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="diaryFeelings">How did this topic make you feel?</label>
                  <textarea
                    id="diaryFeelings"
                    value={diaryFeelings}
                    onChange={(e) => setDiaryFeelings(e.target.value)}
                    className="form-control"
                    placeholder="Share your thoughts and feelings about the topic..."
                    rows={4}
                  />
                </div>
                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={savingDiary || (!diarySkills.trim() && !diaryFeelings.trim())}
                  >
                    {savingDiary ? 'Saving...' : 'Save Reflection'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {activeTab === 'checkpoints' && (
            <div className="assignment-section checkpoints-section">
              <h3>Checkpoints</h3>
              <p className="section-description">
                Track your progress through the assignment by marking these checkpoints as completed.
              </p>
              <div className="checkpoints-list">
                {[
                  { key: 'diaryCompleted', label: 'Complete the reflection diary' },
                  { key: 'assignmentUploaded', label: 'Upload assignment submission' },
                  { key: 'webinar1Attended', label: 'Attend webinar 1' },
                  { key: 'webinar2Attended', label: 'Attend webinar 2' },
                  { key: 'feedbackReviewed', label: 'Review feedback and make updates if needed' }
                ].map(({ key, label }) => (
                  <div key={key} className="checkpoint-item">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={checkpoints[key]}
                        onChange={() => toggleCheckpoint(key)}
                      />
                      <span>{label}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'feedback' && (
            <div className="assignment-section feedback-section">
              <h3>Mentor Feedback</h3>
              <div className="feedback-container">
                {feedback ? (
                  <>
                    <div className="feedback-header">
                      <span className="feedback-date">
                        Provided on: {feedback.createdAt?.toLocaleDateString?.() ||
                          (feedback.createdAt ? new Date(feedback.createdAt).toLocaleDateString() : 'Unknown date')}
                      </span>
                      <span className="mentor-name">By: {feedback.mentorName || 'Your Mentor'}</span>
                    </div>
                    <div className="feedback-positive">
                      <h4>What Was Good</h4>
                      <div className="feedback-content-block">
                        {feedback.positivePoints || 'No specific positive points provided.'}
                      </div>
                    </div>
                    <div className="feedback-improvement">
                      <h4>Areas for Improvement</h4>
                      <div className="feedback-content-block">
                        {feedback.improvementAreas || 'No specific improvement areas provided.'}
                      </div>
                    </div>
                  </>
                ) : submission?.mentorFeedback ? (
                  <>
                    <div className="feedback-header">
                      <span className="feedback-date">
                        Provided on: {submission.mentorFeedback.updatedAt
                          ? (typeof submission.mentorFeedback.updatedAt.toLocaleDateString === 'function'
                            ? submission.mentorFeedback.updatedAt.toLocaleDateString()
                            : new Date(submission.mentorFeedback.updatedAt).toLocaleDateString())
                          : 'Unknown date'}
                      </span>
                    </div>
                    <div className="feedback-positive">
                      <h4>What Was Good</h4>
                      <div className="feedback-content-block">
                        {submission.mentorFeedback.positivePoints || 'No specific positive points provided.'}
                      </div>
                    </div>
                    <div className="feedback-improvement">
                      <h4>Areas for Improvement</h4>
                      <div className="feedback-content-block">
                        {submission.mentorFeedback.improvementAreas || 'No specific improvement areas provided.'}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="no-feedback-message">
                    No feedback is available yet. Your mentor will provide feedback soon.
                  </div>
                )}

                <div className="form-group review-toggle">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={checkpoints.feedbackReviewed}
                      onChange={() => toggleCheckpoint('feedbackReviewed')}
                    />
                    <span>I have reviewed this feedback</span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bottom-space"></div>
      <BottomNavigation />
    </div>
  );
}

export default AssignmentView;